import { Html5VideoPipeline, Html5CanvasPipeline } from 'media-stream-library';
import { v4 } from 'uuid';
import { CameraOpts } from './rtsp-h264-mjpeg-interface';

export const DEFAULT_UPLINK = 'wss://video-broker.larva.io/';

export class Camera {
  readonly options: CameraOpts;
  readonly server: string;
  readonly wsurl: string;
  readonly id: string;
  readonly $el: HTMLVideoElement | HTMLCanvasElement;

  private isH264: boolean;
  private pipeline?: Html5VideoPipeline | Html5CanvasPipeline;
  private isLoading = true;
  private isOpened = false;

  constructor(opts: CameraOpts, server: string) {
    this.options = opts;
    this.id = v4();
    this.isH264 = !!window.MediaSource && !!opts.h264;
    this.$el = this.isH264 ? document.createElement('video') as HTMLVideoElement : document.createElement('canvas') as HTMLCanvasElement;
    this.$el.setAttribute(Camera.elementCameraIdAttribute, this.id);
    if (this.isH264) {
      // !important, do not use poster attribute,
      // it's not working on ios 15 properly
      // https://developer.apple.com/forums/thread/690523
      (this.$el as HTMLVideoElement).autoplay = true;
      (this.$el as HTMLVideoElement).preload = 'none';
      (this.$el as HTMLVideoElement).playsInline = true;
      (this.$el as HTMLVideoElement).style.background = 'transparent';
      this.mute();
    }
    // impoprtant
    this.$el.style.width = '100%';
    this.server = server;
    const url = new URL(this.server);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/${this.id}`;
    this.wsurl = url.href;
  }

  static getLocalVideoServer() {
    const protocol = location.protocol === 'http:' ? 'ws://' : 'wss://';
    const port = window.location.port !== '' ? (':' + window.location.port) : '';
    const host = window.location.hostname;
    const path = '/video-proxy';
    return `${protocol}${host}${port}${path}`;
  }

  static get elementCameraIdAttribute() {
    return 'camera-id';
  }

  public async open() {
    if (this.isOpened) {
      // opened
      return;
    }
    try {
      this.isOpened = true;
      const options = {
        ...(!!this.options.username || !!this.options.password ) ? { auth: { username: this.options.username, password: this.options.password } } : {},
        ws: { uri: this.wsurl, tokenUri: null, timeout: 5000 },
        rtsp: { uri: this.isH264 ? this.options.h264 : this.options.mjpeg },
        mediaElement: this.$el,
      };
      this.pipeline = this.isH264 ?
        new Html5VideoPipeline({ ...options, mediaElement: options.mediaElement as HTMLVideoElement }) :
        new Html5CanvasPipeline({ ...options, mediaElement: options.mediaElement as HTMLCanvasElement });
        // cancvas element does now know about drawing events or what so ever
        // so lets see if time has started running
      const interval = setInterval(() => {
        const currentTime = Array.isArray(this.pipeline.currentTime) ? this.pipeline.currentTime[0] : this.pipeline.currentTime;
        const bitrate = Array.isArray(this.pipeline.bitrate) ? this.pipeline.bitrate[0] : this.pipeline.bitrate;
        const framerate = Array.isArray(this.pipeline.framerate) ? this.pipeline.framerate[0] : this.pipeline.framerate;
        if (currentTime > 0 || bitrate > 0 || framerate > 0) {
          this.loading = false;
          clearInterval(interval);
        }
      }, 500);
      await this.pipeline.ready;
      this.pipeline.rtsp.play();
      // fixes: https://developer.apple.com/forums/thread/129377
      if (this.isH264) {
        (this.$el as HTMLVideoElement).pause();
        (this.$el as HTMLVideoElement).play();
      }
    } catch (err) { // supress
      console.error(err);
      throw new Error('Failed to connect to video server');
    }
  }

  public mute() {
    if (this.isH264) {
      (this.$el as HTMLVideoElement).muted = true;
      (this.$el as HTMLVideoElement).volume = 0;
    }
  }

  public volume(volume = 1) {
    if (this.isH264) {
      (this.$el as HTMLVideoElement).muted = false;
      (this.$el as HTMLVideoElement).volume = volume;
    }
  }

  public close() {
    this.pipeline?.close();
    this.$el?.remove();
  }

  set loading(val: boolean) {
    const event = new CustomEvent('larvideoloading', { detail: val });
    this.$el?.dispatchEvent(event);
    this.isLoading = val;
  }

  get loading() {
    return this.isLoading;
  }

  get opened() {
    return this.isOpened;
  }
}